/** Phase E (cks package): Liberty Square / CKS Memorial Hall and Beitou hot springs. Lines marked “English practice” stay in English in every locale. */
export const enCks = {
  cks: {
    mapNames: { cks: 'Liberty Square', beitou: 'Beitou Hot Springs' },
    zones: {
      ck_hall: 'CKS Memorial Hall', ck_gate: 'Liberty Square Gate', ck_theater: 'National Theater', ck_concert: 'National Concert Hall',
      ck_guanghua: 'Guanghua Pond', ck_yunhan: 'Yunhan Pond', ck_square: 'Liberty Square', ck_garden: 'Memorial Hall gardens',
      ck_valley: 'Thermal Valley', ck_millennium: 'Millennium Hot Spring', ck_plumGarden: 'Plum Garden', ck_library: 'Beitou Library',
      ck_museum: 'Hot Spring Museum', ck_btStation: 'Xinbeitou Station', ck_hotels: 'Hot-spring hotels', ck_park: 'Beitou Park',
    },
    objects: {
      ck_mrt: 'MRT: CKS Memorial Hall Station', ck_gate: 'Photo spot: Liberty Square Gate', ck_steps: 'The 89 steps', ck_guard: 'Changing of the guard',
      ck_hall: 'Memorial Hall plaque', ck_theater: 'National Theater', ck_concert: 'National Concert Hall', ck_koi: 'Guanghua Pond koi',
      ck_yunhan: 'Yunhan Pond', ck_taichi: 'Morning tai chi', ck_kite: 'Kite seller', ck_snack: 'Egg cake cart', ck_bubble: 'Bubble tea stand',
      ck_corridor: 'Corridor notice board', ck_dance: 'Evening dance group', ck_pigeons: 'Pigeons',
      ck_btMrt: 'MRT: Xinbeitou Station', ck_oldStation: 'Historic Xinbeitou Station', ck_library: 'Beitou Public Library',
      ck_museum: 'Beitou Hot Spring Museum', ck_bath: 'Millennium Hot Spring', ck_footbath: 'Free foot bath', ck_valley: 'Thermal Valley',
      ck_eggs: 'Hot-spring eggs', ck_plum: 'Plum Garden', ck_stream: 'Beitou Stream',
    },
    npc: {
      ck_guard: 'Honour guard', ck_guide: 'Tour guide', ck_taichi: 'Tai chi master', ck_taichi2: 'Tai chi grandma', ck_kiteUncle: 'Kite uncle',
      ck_dancer: 'Dance leader', ck_teacher: 'School-trip teacher', ck_tourist: 'Backpacker', ck_photographer: 'Photographer',
      ck_koiGrandma: 'Koi grandma', ck_student: 'University student', ck_volunteer: 'Volunteer guide',
      ck_librarian: 'Librarian', ck_attendant: 'Bath attendant', ck_eggAuntie: 'Egg auntie', ck_hikerA: 'Hiker', ck_hikerB: 'Hiking grandpa',
      ck_footGrandpa: 'Grandpa Lin', ck_museumGuide: 'Museum guide', ck_stationVol: 'Station volunteer', ck_tourist2: 'Visitor from Japan', ck_kid: 'Xiaoan',
    },
    items: {} as Record<string, { name: string; desc: string }>,

    // --- Shared bits -----------------------------------------------------------------------------
    listen: '🔊 Listen again',
    next: '▶ Next',
    ok: 'OK!',
    notNow: 'Not now',
    learned: 'You learned something new! ⭐',
    poor: 'You don’t have enough coins.',

    // --- Liberty Square ----------------------------------------------------------------------------
    welcomeCks: '🏯 Liberty Square! The white hall with the blue roof is at the far end, up 89 steps.',
    gate: {
      text: 'The Liberty Square Gate is 30 metres tall and 80 metres wide. It has five openings and blue tiled roofs. The big words 自由廣場 mean “Liberty Square”.',
      photo: '📸 Ask someone to take your photo',
      how: 'How do you ask? (English practice)',
      ask: ['Excuse me, could you take a photo of me, please?', 'Hey. Photo. Me. Now.', 'You take picture, I stand.'], // English practice
      wrong: 'The tourist hesitates: “Er… could you ask more politely?”',
      done: '“Say cheese!” 📸 Click! A perfect photo with the gate behind you.',
    },
    steps: {
      text: 'There are 89 steps up to the Memorial Hall — one for each year of Chiang Kai-shek’s life. Can you climb them all?',
      climb: '🧗 Climb the 89 steps',
      quiz: '❓ Quiz',
      counting: '“…eighty-seven, eighty-eight, eighty-nine!” You made it to the top! The whole square lies below you, all the way to the gate.', // English practice
      countEn: 'Eighty-seven, eighty-eight, eighty-nine! I made it!', // English practice
      tired: 'You’re too tired to climb 89 steps. Rest a little first!',
      q: 'How many steps are there?', // English practice
      a: ['Eighty-nine', 'Forty-two', 'One hundred and one'], // English practice
      right: 'Right! Eighty-nine steps. Good counting!',
      wrong: 'Not quite. Count again… one, two, three…',
    },
    guard: {
      notNow: (next: string) => `The honour guards change every hour, on the hour, from 9 in the morning to 5 in the afternoon. The next one is at ${next}. Come back then!`,
      closed: 'The ceremony is over for today. The guards change every hour from 9:00 to 17:00. Come back tomorrow!',
      intro: '🎖️ It’s the changing of the guard! The crowd goes quiet. Listen to the commentary:',
      lines: [
        'The new guards march out slowly, step by step. Their boots click on the marble.', // English practice
        'Now they spin their rifles in the air — and they never drop them!', // English practice
        'The old guards and the new guards salute each other.', // English practice
        'The new guards stand completely still. They must not move, talk or smile for one hour!', // English practice
      ],
      done: 'What a show! The guards train for months to move together like one person.',
      watch: '👀 Watch',
    },
    hall: {
      text: 'The National Chiang Kai-shek Memorial Hall is 70 metres tall, about as tall as a 20-floor building. It is made of white marble, with a blue glazed roof.',
      q: 'How many sides does the roof have?', // English practice
      a: ['Eight', 'Six', 'Four'], // English practice
      right: 'Yes! The roof is octagonal: it has eight sides, like a stop sign.',
      wrong: 'Look again at the roof and count its sides!',
    },
    theater: {
      text: 'The National Theater looks like an ancient palace, with a golden roof and red pillars. Opera, ballet and plays happen here.',
      ask: '🎭 Ask about tickets',
      vendor: 'Hello! Can I help you?', // English practice
      ok: 'Two tickets for tonight’s show, please.', // English practice
      bad: ['Give ticket.', 'Show. Two. Me.'], // English practice
      done: '“Here you are: two tickets, row C. The show starts at seven thirty!” (You keep them as a souvenir.)', // English practice
    },
    concert: {
      text: 'The National Concert Hall is the theater’s twin. Orchestras, choirs and pianists play here.',
      listen: '🎻 Listen at the door',
      heard: 'Through the doors you hear violins practising. Beautiful! 🎶',
      phrase: 'What time does the concert start? — It starts at seven o’clock.', // English practice
    },
    koi: {
      text: 'Guanghua Pond is full of koi: orange, white, black and gold. Some koi live for more than 50 years!',
      feed: '🐟 Buy fish food and feed the koi (5 coins)',
      fed: 'Splash! Dozens of koi rush over, opening their mouths: “O! O! O!”',
      en: 'Look! The koi are opening their mouths!', // English practice
      already: 'The koi are full now. Don’t feed them too much!',
    },
    yunhan: {
      text: 'Yunhan Pond means “Milky Way Pond”. A little bridge crosses it, and turtles sunbathe on the rocks.',
      en: 'Look, there’s a turtle on the rock!', // English practice
      q: 'What animal is sunbathing on the rock?', // English practice
      a: ['A turtle', 'A cat', 'A pigeon'], // English practice
      right: 'Yes, a turtle! It’s warming up in the sun.',
      wrong: 'Look closer: it has a shell!',
    },
    taichi: {
      text: 'Every morning, grandpas and grandmas practise tai chi here. They move slowly, like water.',
      join: '🧘 Join in',
      en: 'Breathe in… breathe out… move your arms slowly, like a cloud.', // English practice
      done: 'You feel calm and awake. Your body is warm!',
      later: 'The tai chi group meets every morning from 6 to 10. Come back early!',
      already: 'You already did tai chi today. Your legs say thank you!',
    },
    kite: {
      text: 'The kite uncle sells colourful kites: dragons, butterflies and fish. There is lots of wind on the open square.',
      buy: '🪁 Buy and fly a kite (15 coins)',
      en: 'The wind is strong today! Let out more string!', // English practice
      done: 'Your kite climbs higher and higher, its long tail dancing in the wind!',
      words: 'New words: kite · string · tail · wind', // English practice
    },
    snack: {
      vendor: 'Hi! Egg cakes, fresh and warm. What would you like?', // English practice
      ok: 'Can I have a bag of egg cakes, please?', // English practice
      bad: ['Egg cake. Give.', 'I want eat cake now.'], // English practice
      done: 'Here you go! Careful, they’re hot. Enjoy!', // English practice
    },
    bubble: {
      vendor: 'Hello! What can I get you?', // English practice
      ok: 'A medium bubble milk tea with less sugar, please.', // English practice
      bad: ['Tea! Big! Now!', 'Bubble give me.'], // English practice
      done: 'One medium bubble milk tea, less sugar. Here’s your straw!', // English practice
    },
    corridor: {
      intro: 'A notice board in the blue-tiled corridor has a quiz for visitors.',
      qs: [
        { q: 'What colour is the Memorial Hall’s roof?', a: ['Blue', 'Red', 'Green'] }, // English practice
        { q: 'Which building has a golden roof?', a: ['The National Theater', 'The MRT station', 'The gate'] }, // English practice
        { q: 'Where do the koi live?', a: ['In the ponds', 'In the trees', 'On the roof'] }, // English practice
      ],
      right: 'Correct! ✅',
      wrong: 'Hmm, not that one. Try again!',
      done: 'You answered every question! The board says: “Thank you for visiting!”',
    },
    dance: {
      text: 'Every evening, groups of dancers practise in front of the theater, with a little speaker playing music.',
      join: '💃 Join the dance',
      en: 'Step to the left, step to the right, and turn around!', // English practice
      done: 'You danced with the group! The aunties clap for you.',
      later: 'The dance groups come in the evening, from 6 to 10 p.m. They practise for shows and competitions!',
    },
    pigeons: {
      text: 'Hundreds of pigeons walk around the square. When something surprises them, they all fly up at once!',
      watch: '👀 Watch the pigeons',
      en: 'Please don’t chase the pigeons!', // English practice
      done: 'Flap flap flap! A whole cloud of pigeons circles the square and lands again.',
    },

    // --- Beitou ------------------------------------------------------------------------------------
    welcomeBeitou: '♨️ Welcome to Beitou! Can you smell the hot springs? Steam rises from the stream.',
    oldStation: {
      text: 'This little wooden station was built in 1916. It was moved away in 1988 and brought back home in 2017. Now it’s a small museum.',
      en: 'This station is over one hundred years old.', // English practice
      q: 'What is the old station made of?', // English practice
      a: ['Wood', 'Stone', 'Glass'], // English practice
      right: 'Yes, wood! Japanese-style wooden boards and a tiled roof.',
      wrong: 'Look at the walls: brown boards…',
    },
    library: {
      text: 'Beitou Library was Taiwan’s first green library. It is made of wood, has solar panels on its roof, and collects rainwater to flush the toilets!',
      read: '📖 Read for a while',
      readDone: 'You read a book about volcanoes under the wooden balcony. Very quiet, very nice.',
      en: 'Excuse me, where can I find books in English?', // English practice
      reply: '“They’re on the second floor, next to the window.”', // English practice
      ask: '🗣️ Ask the librarian',
      q: 'What do the solar panels on the roof make?', // English practice
      a: ['Electricity', 'Water', 'Books'], // English practice
      right: 'Right! Sunlight becomes electricity.',
      wrong: 'Think: the sun shines on the panels and makes…',
      quiz: '❓ Quiz',
    },
    museum: {
      text: 'The Hot Spring Museum was built in 1913 as a public bathhouse — the biggest in East Asia. Downstairs (red brick) is the big Roman-style bath; upstairs (wood) is a tatami room for resting.',
      en: 'Is the museum free? — Yes, it’s free!', // English practice
      q: 'What was the museum before?', // English practice
      a: ['A public bathhouse', 'A train station', 'A school'], // English practice
      right: 'Exactly! People came here to bathe more than a hundred years ago.',
      wrong: 'Hint: it has a big bath downstairs…',
    },
    bath: {
      text: 'The Millennium Hot Spring is an open-air public bath beside the stream. Everyone wears a swimsuit, and the pools go from warm to very hot.',
      go: '♨️ Bathe for an hour (40 coins)',
      rule: 'The attendant asks: “What must you wear in the pools?”', // English practice
      a: ['A swimsuit', 'My school uniform', 'A raincoat'], // English practice
      wrong: '“No, no! Try again.”',
      done: 'You shower first, then slowly slide into the warm pool. Ahh… Your whole body relaxes. 😌',
      en: 'Please take a shower before you get in the pool.', // English practice
      already: 'You already bathed today. Too much hot water makes you dizzy!',
      closed: 'The Millennium Hot Spring is closed now. It opens at 5:30 in the morning.',
    },
    footbath: {
      text: 'A free public foot bath! Sit on the wooden edge and put your feet in the warm water.',
      soak: '🦶 Soak your feet',
      phrases: [
        'The water is hot! Let me put my feet in slowly.', // English practice
        'It’s warm and cosy. I love it!', // English practice
        'Ouch, it’s too hot! Is it forty-two degrees?', // English practice
        'Ahh, my feet feel so good after walking all day.', // English practice
      ],
      done: 'Your feet turn pink and warm. You feel rested! (+energy, +mood)',
      already: 'Your feet are already wrinkly like raisins. Once a day is enough!',
      q: 'The water is 42°C. Is it hot or cold?', // English practice
      a: ['It’s hot', 'It’s cold', 'It’s frozen'], // English practice
      right: 'Yes, it’s hot! Hot · warm · cool · cold.', // English practice
      wrong: 'Hmm, 42 degrees is more than your body (37)…',
      quiz: '🌡️ Temperature quiz',
    },
    valley: {
      text: 'Thermal Valley: jade-green water bubbles up at up to 90°C! The steam smells of sulphur, like rotten eggs. People once called it “Hell Valley”.',
      en: 'Danger! Do not touch the water. It’s very hot!', // English practice
      q: 'How hot can the water in Thermal Valley get?', // English practice
      a: ['About ninety degrees', 'About twenty degrees', 'About zero degrees'], // English practice
      right: 'Yes, nearly boiling! That’s why there’s a railing.',
      wrong: 'Look at the steam and the warning signs again…',
      fact: 'A rare green mineral called Beitou stone (Hokutolite) grows here. It’s found in only two places in the world!',
    },
    eggs: {
      vendor: 'Hot-spring eggs! Cooked in the hot water. How many would you like?', // English practice
      ok: 'Three eggs, please.', // English practice
      bad: ['Egg. Three. Now.', 'Give me egg you.'], // English practice
      done: 'Here you are, three warm eggs with a little salt. The yolk is soft and creamy!', // English practice
    },
    plum: {
      text: 'The Plum Garden (梅庭) was the summer house of the calligrapher Yu Youren. The plum blossom is Taiwan’s national flower.',
      en: 'The plum blossom is the national flower of Taiwan.', // English practice
      q: 'When do plum flowers bloom?', // English practice
      a: ['In winter', 'In summer', 'Never'], // English practice
      right: 'Yes! They bloom in the cold of winter, so they mean being brave and strong.',
      wrong: 'Hint: they bloom when it’s cold…',
    },
    stream: {
      text: 'Beitou Stream is warm! Hot-spring water flows down from Thermal Valley, and steam floats over it on cool days.',
      en: 'What’s that smell? — It’s sulphur from the hot springs.', // English practice
    },

    // --- People ------------------------------------------------------------------------------------
    talk: {
      ck_guard: [
        { text: 'I’m on a break. Inside the hall, we stand still for one hour. No moving, no smiling!' },
        { text: 'Our rifles weigh about four kilos. We spin them every day in practice.' },
        { text: 'Visitors ask me: “How do you stand so still?” I say: “Practice, practice, practice!”', en: 'Practice, practice, practice!' }, // English practice
      ],
      ck_guide: [
        { text: 'Follow the yellow flag, everyone! This way to the Memorial Hall.', en: 'Follow me, please! This way.' }, // English practice
        { text: 'The theater and the concert hall face each other across the square, like twins.' },
        { text: 'Tip: the hall is beautiful at night, when it’s lit up in white.' },
      ],
      ck_taichi: [
        { text: 'Good morning! Tai chi keeps me young. I’m eighty-two!', en: 'Good morning! How are you today?' }, // English practice
        { text: 'Bend your knees a little… slowly push the air, like this.' },
      ],
      ck_taichi2: [
        { text: 'We come every morning at six, rain or shine.', en: 'Rain or shine!' }, // English practice
        { text: 'After tai chi, we all go for soy milk and egg pancakes. Delicious!' },
      ],
      ck_kiteUncle: [
        { text: 'Run against the wind and let the string out little by little!', en: 'Let the string out little by little.' }, // English practice
        { text: 'My best kite is a ten-metre dragon. It needs three people to fly it!' },
      ],
      ck_dancer: [
        { text: 'Five, six, seven, eight! Again, from the top!', en: 'Five, six, seven, eight!' }, // English practice
        { text: 'We’re practising for a competition next month. Want to join us?' },
      ],
      ck_teacher: [
        { text: 'Class, stay together, please! Don’t run on the steps!', en: 'Stay together, please!' }, // English practice
        { text: 'Today we’re learning about Taiwan’s history and its buildings.' },
      ],
      ck_tourist: [
        { text: 'Hi! I’m from Canada. Where is the MRT station?', en: 'Excuse me, where is the MRT station?' }, // English practice
        { text: 'Is the Memorial Hall free to visit? — Yes, it is! Thanks!', en: 'Is it free to visit?' }, // English practice
        { text: 'I love bubble tea. In English we say “bubble tea” or “boba”!' }, // English practice
      ],
      ck_photographer: [
        { text: 'The best photo is from here: the whole gate in the frame, with blue sky behind.' },
        { text: 'Smile and say “cheese”!', en: 'Smile and say cheese!' }, // English practice
      ],
      ck_koiGrandma: [
        { text: 'That big orange koi is called Lucky. I’ve fed him for ten years!' },
        { text: 'Koi colours: orange, white, black, gold. Which is your favourite?', en: 'Which colour is your favourite?' }, // English practice
      ],
      ck_student: [
        { text: 'I study in the corridor. It’s cool and quiet, with a breeze.' },
        { text: 'I’m practising English for an exam. “How long have you lived in Taipei?”', en: 'How long have you lived in Taipei?' }, // English practice
      ],
      ck_volunteer: [
        { text: 'Welcome! The hall opens at nine and closes at six.', en: 'The hall opens at nine and closes at six.' }, // English practice
        { text: 'The blue tiles and white marble make the building look calm and grand.' },
      ],
      ck_librarian: [
        { text: 'Shh! This is a library. Please speak quietly.', en: 'Please speak quietly.' }, // English practice
        { text: 'The big wooden balconies keep the sun out, so we need less air conditioning.' },
      ],
      ck_attendant: [
        { text: 'Shower first, swimsuit on, and no towels in the water!', en: 'Please shower first.' }, // English practice
        { text: 'The hottest pool is at the top, about forty-four degrees. Be careful!' },
      ],
      ck_eggAuntie: [
        { text: 'My eggs are cooked in hot-spring water. Taste the minerals!' },
        { text: 'How many eggs? One, two, three… I can count in English!', en: 'How many eggs would you like?' }, // English practice
      ],
      ck_hikerA: [
        { text: 'I hiked down from Yangmingshan. There are volcanoes up there!' },
        { text: 'Don’t forget water and a hat when you go hiking.', en: 'Don’t forget your water bottle!' }, // English practice
      ],
      ck_hikerB: [
        { text: 'Seventy years old and I still climb every weekend!' },
        { text: 'After a hike, a foot bath is the best reward.', en: 'What a beautiful day for a walk!' }, // English practice
      ],
      ck_footGrandpa: [
        { text: 'Come, sit! The water is just right today. About forty degrees.', en: 'The water is just right.' }, // English practice
        { text: 'When I was a boy, the hot springs here were full of steam and singing.' },
        { text: 'Hot, warm, cool, cold. Say it with me!', en: 'Hot, warm, cool, cold.' }, // English practice
      ],
      ck_museumGuide: [
        { text: 'Take off your shoes before you go upstairs to the tatami room.', en: 'Please take off your shoes.' }, // English practice
        { text: 'The stained-glass windows downstairs are more than a hundred years old.' },
      ],
      ck_stationVol: [
        { text: 'The old station had a little train that took bathers from Beitou to here.' },
        { text: 'Look up at the roof window. It lets light into the waiting room.' },
      ],
      ck_tourist2: [
        { text: 'Konnichiwa! Beitou feels like a hot-spring town in Japan.' },
        { text: 'I always try the hot-spring eggs. They’re so good!', en: 'They are so good!' }, // English practice
      ],
      ck_kid: [
        { text: 'The stream is warm! Mum says don’t put my hands in it.' },
        { text: 'Why does it smell like eggs? — Sulphur! I learned that word today.', en: 'It smells like eggs!' }, // English practice
      ],
    } as Record<string, { text: string; en?: string }[]>,
  },
};
